import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { trackSignup } from "@/lib/analytics";
import type { User, Session } from "@supabase/supabase-js";

export type SubscriptionTier = "free" | "pro" | "elite";

interface SubscriptionInfo {
  subscribed: boolean;
  tier: SubscriptionTier;
  subscriptionEnd: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  subscription: SubscriptionInfo;
  checkSubscription: () => Promise<void>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const defaultSub: SubscriptionInfo = { subscribed: false, tier: "free", subscriptionEnd: null };

// Attribute a pending referral code (set by ReferralCapture in App.tsx from ?ref=)
// to a freshly-signed-up user. Fires on SIGNED_IN so it covers EVERY signup path —
// email/password confirmation, OAuth, and magic link — not just the OAuth callback.
// Guards: only new accounts (created < 24h ago) so a returning user carrying a stale
// code can't be mis-attributed. record-referral is itself idempotent and blocks
// self-referral, so a redundant call from AuthCallbackPage is harmless.
async function recordReferralIfPresent(user: User | null | undefined) {
  const code = localStorage.getItem("mm-referral-code");
  if (!code || !user) return;
  const isNewAccount =
    Date.now() - new Date(user.created_at).getTime() < 24 * 60 * 60 * 1000;
  if (!isNewAccount) return;
  try {
    await supabase.functions.invoke("record-referral", { body: { code } });
  } catch {
    // non-critical — never block auth on referral bookkeeping
  } finally {
    localStorage.removeItem("mm-referral-code");
  }
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  subscription: defaultSub,
  checkSubscription: async () => {},
  signUp: async () => ({ error: null }),
  signIn: async () => ({ error: null }),
  signOut: async () => {},
  resetPassword: async () => ({ error: null }),
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionInfo>(defaultSub);

  const checkSubscription = useCallback(async () => {
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      setSubscription({
        subscribed: data?.subscribed ?? false,
        tier: (data?.tier as SubscriptionTier) ?? "free",
        subscriptionEnd: data?.subscription_end ?? null,
      });
    } catch (err) {
      console.error("Subscription check failed:", err);
      // Keep current state on error
    }
  }, []);

  useEffect(() => {
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (event === "TOKEN_REFRESHED" && !session) {
        // Session expired and couldn't refresh
        window.location.href = "/auth";
        return;
      }
      if (event === "SIGNED_OUT") {
        setSubscription(defaultSub);
        return;
      }
      if (session?.user) {
        // Defer to avoid Supabase auth deadlock
        setTimeout(() => checkSubscription(), 0);
        if (event === "SIGNED_IN") {
          // Attribute a referral for any newly-authenticated user (all signup paths).
          setTimeout(() => recordReferralIfPresent(session.user), 0);
        }
      } else {
        setSubscription(defaultSub);
      }
    });

    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) checkSubscription();
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("[AuthContext] getSession failed:", e);
        setLoading(false);
      });

    return () => authSub.unsubscribe();
  }, [checkSubscription]);

  // Refresh subscription every 60s
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkSubscription, 60_000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: window.location.origin },
    });
    if (!error) trackSignup(); // ad conversion (idempotent per browser)
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth`,
    });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, subscription, checkSubscription, signUp, signIn, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
}
